import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { trackAddToCart, trackRemoveFromCart } from "../lib/analytics";

const CartContext = createContext(null);
const STORAGE_KEY = "hyppe_cart_v2";
const COUPON_KEY = "hyppe_coupon_v1";

// crypto.randomUUID() only exists in secure contexts (HTTPS/localhost). On a
// non-secure VPS (plain HTTP / bare IP) it is undefined and throws, crashing
// Add to Cart. Use it when available, else fall back to a unique enough id.
const genKey = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [pricing, setPricing] = useState({ shipping_enabled: true, shipping_flat: 79, free_shipping_threshold: 999, tax_rate: 0 });
  const [coupon, setCoupon] = useState(() => {
    try {
      const raw = localStorage.getItem(COUPON_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => {
    if (coupon) localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
    else localStorage.removeItem(COUPON_KEY);
  }, [coupon]);

  useEffect(() => {
    api.get("/scents").then((r) => r.data.pricing && setPricing(r.data.pricing)).catch(() => {});
  }, []);

  const addItem = useCallback((item) => {
    trackAddToCart(item);
    setItems((p) => [...p, { ...item, _key: genKey() }]);
  }, []);
  const removeItem = useCallback((key) => {
    const it = itemsRef.current.find((i) => i._key === key);
    if (it) trackRemoveFromCart(it);
    setItems((p) => p.filter((i) => i._key !== key));
  }, []);
  const updateQty = useCallback((key, qty) =>
    setItems((p) => p.map((i) => (i._key === key ? { ...i, quantity: Math.max(1, qty) } : i))), []);
  const clearCart = useCallback(() => { setItems([]); setCoupon(null); }, []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const applyCoupon = useCallback(async (code, customerEmail = null) => {
    const clean = (code || "").trim();
    if (!clean) return { ok: false, message: "Enter a coupon code" };
    try {
      const { data } = await api.post("/coupons/validate", {
        code: clean,
        subtotal,
        customer_email: customerEmail || null,
      });
      if (data.valid) {
        setCoupon({
          code: data.coupon.code,
          discount: data.discount,
          type: data.coupon.type,
          value: data.coupon.value,
          message: data.message,
          customerEmail: customerEmail || null,
        });
        return { ok: true, message: data.message, discount: data.discount };
      }
      setCoupon(null);
      return { ok: false, message: data.message };
    } catch {
      setCoupon(null);
      return { ok: false, message: "Could not validate coupon" };
    }
  }, [subtotal]);

  const removeCoupon = useCallback(() => setCoupon(null), []);

  // Re-validate coupon when subtotal changes (qty edits) to keep discount accurate
  useEffect(() => {
    if (!coupon?.code) return;
    let active = true;
    api.post("/coupons/validate", {
      code: coupon.code,
      subtotal,
      customer_email: coupon.customerEmail || null,
    }).then(({ data }) => {
      if (!active) return;
      if (data.valid) setCoupon((c) => (c ? { ...c, discount: data.discount } : c));
      else setCoupon(null);
    }).catch(() => {});
    return () => { active = false; };
  }, [subtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const discount = Math.min(coupon?.discount || 0, subtotal);
  const taxable = Math.max(0, subtotal - discount);
  const shipping = pricing.shipping_enabled === false || subtotal === 0 || subtotal >= pricing.free_shipping_threshold ? 0 : pricing.shipping_flat;
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const tax = round2(taxable * (pricing.tax_rate || 0) / 100);
  const total = round2(taxable + shipping + tax);

  const value = useMemo(
    () => ({
      items, addItem, removeItem, updateQty, clearCart,
      subtotal, discount, shipping, tax, total, count,
      coupon, applyCoupon, removeCoupon, pricing,
      FREE_SHIP_THRESHOLD: pricing.free_shipping_threshold,
      shippingFlat: pricing.shipping_flat,
      shippingEnabled: pricing.shipping_enabled !== false,
    }),
    [items, addItem, removeItem, updateQty, clearCart, subtotal, discount, shipping, tax, total, count, coupon, applyCoupon, removeCoupon, pricing]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
